import { useState, useEffect, useMemo, useCallback } from 'react';
import { api, formatCurrency } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  Plus, Check, X, Camera, Loader2, Trash2, Search, Edit3,
  ChefHat, Package, Save, Sparkles, UtensilsCrossed, PieChart,
  Upload, Copy, ClipboardPaste
} from 'lucide-react';
import { toast } from 'sonner';

// ── Category & target config ──────────────────────────────────────
const CATEGORY_GROUPS = [
  { id: 'all', label: 'Tous' },
  { id: 'entrees', label: 'Entrees', cats: ['entree'] },
  { id: 'plats', label: 'Plats', cats: ['plat_viande', 'plat_poisson'] },
  { id: 'tapas', label: 'Tapas', cats: ['tapas'] },
  { id: 'desserts', label: 'Desserts', cats: ['dessert'] },
  { id: 'boissons', label: 'Boissons', cats: ['vin_blanc', 'vin_rouge', 'vin_rose', 'champagne', 'aperitif', 'biere', 'soft', 'spiritueux', 'cafe', 'boisson'] },
];

const FOOD_COST_TARGETS = {
  plat_viande: { min: 28, max: 33 },
  plat_poisson: { min: 28, max: 33 },
  entree: { min: 20, max: 26 },
  tapas: { min: 18, max: 25 },
  dessert: { min: 22, max: 28 },
};
const DEFAULT_TARGET = { min: 25, max: 35 };

const getTarget = (cat) => FOOD_COST_TARGETS[cat] || DEFAULT_TARGET;

const ratioStatus = (ratio, cat) => {
  if (ratio <= 0) return 'none';
  const t = getTarget(cat);
  if (ratio <= t.max) return 'green';
  if (ratio <= t.max + 5) return 'orange';
  return 'red';
};

const statusColor = (s) => ({
  green: 'text-[rgb(var(--lm-green))]',
  orange: 'text-orange-400',
  red: 'text-[rgb(var(--lm-red))]',
  none: 'text-[rgb(var(--lm-text-muted))]',
}[s] || 'text-[rgb(var(--lm-text-muted))]');

const statusBarBg = (s) => ({
  green: 'bg-[rgb(var(--lm-green))]',
  orange: 'bg-orange-400',
  red: 'bg-red-400',
  none: 'bg-[rgb(var(--lm-border))]',
}[s] || 'bg-[rgb(var(--lm-border))]');

const SECTIONS = [
  'Entrees', 'Entrées', 'Tapas', 'Viandes', 'Plats Viande', 'Poissons', 'Plats Poisson',
  'Menu Enfant', 'Garnitures', 'Desserts',
  'Vins Blancs', 'Vins Roses', 'Vins Rosés', 'Vins Rouges',
  'Boissons', 'Champagne', 'Aperitifs', 'Apéritifs', 'Bieres', 'Bières',
  'Softs', 'Spiritueux', 'Cafes', 'Cafés',
];

const CAT_LABELS = {
  entree: 'Entrees', tapas: 'Tapas', plat_viande: 'Viandes',
  plat_poisson: 'Poissons', dessert: 'Desserts',
  vin_blanc: 'Vins Blancs', vin_rouge: 'Vins Rouges', vin_rose: 'Vins Roses',
};

const filterItems = (items, groupId) => {
  if (groupId === 'all') return items;
  const grp = CATEGORY_GROUPS.find(g => g.id === groupId);
  if (!grp?.cats) return items;
  return items.filter(i => grp.cats.includes(i.categorie));
};

// ── Category filter pills ─────────────────────────────────────────
const CategoryFilter = ({ value, onChange, counts }) => (
  <div className="flex gap-2 flex-wrap" data-testid="category-filter">
    {CATEGORY_GROUPS.map(g => {
      const count = g.id === 'all' ? Object.values(counts).reduce((a, b) => a + b, 0) : (counts[g.id] || 0);
      return (
        <button key={g.id} onClick={() => onChange(g.id)}
          className={`px-3 py-1.5 rounded-full text-xs transition-all ${
            value === g.id
              ? 'bg-lemas-gold/20 text-lemas-gold border border-lemas-gold/30'
              : 'bg-[rgb(var(--lm-surface))] text-[rgb(var(--lm-text-secondary))] hover:bg-[rgb(var(--lm-surface-elevated))] border border-transparent'
          }`}
          data-testid={`filter-${g.id}`}>
          {g.label} <span className="ml-1 opacity-60">{count}</span>
        </button>
      );
    })}
  </div>
);

// ══════════════════════════════════════════════════════════════════
// TAB: CARTE ─ Menu + Fiche technique fusionnés
// ══════════════════════════════════════════════════════════════════
const CarteTab = ({ items, dishes, catFilter, setCatFilter, onRefresh }) => {
  // Carte state
  const [addingItem, setAddingItem] = useState(false);
  const [newItem, setNewItem] = useState({ nom: '', prix_ttc: '', cout_matiere: '', section: 'Entrées', categorie: 'entree', description: '' });
  const [scanLoading, setScanLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [search, setSearch] = useState('');

  // Fiche state
  const [editDish, setEditDish] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [acIdx, setAcIdx] = useState(null);
  const [acResults, setAcResults] = useState([]);
  const [showImport, setShowImport] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [promptText, setPromptText] = useState('');
  const [importJson, setImportJson] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Map dishes by name for quick lookup
  const dishMap = useMemo(() => {
    const m = {};
    (dishes || []).forEach(d => { m[d.nom?.toLowerCase()] = d; });
    return m;
  }, [dishes]);

  const filtered = useMemo(() => {
    let list = filterItems(items, catFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(i => i.nom.toLowerCase().includes(q));
    }
    return list;
  }, [items, catFilter, search]);

  const grouped = useMemo(() => {
    const groups = {};
    filtered.forEach(item => {
      const sec = item.section || CAT_LABELS[item.categorie] || 'Autre';
      if (!groups[sec]) groups[sec] = [];
      groups[sec].push(item);
    });
    const sorted = {};
    SECTIONS.forEach(s => { if (groups[s]) sorted[s] = groups[s]; });
    Object.keys(groups).forEach(s => { if (!sorted[s]) sorted[s] = groups[s]; });
    return sorted;
  }, [filtered]);

  const counts = useMemo(() => {
    const c = {};
    CATEGORY_GROUPS.forEach(g => {
      if (g.id !== 'all') c[g.id] = filterItems(items, g.id).length;
    });
    return c;
  }, [items]);

  const withFiche = (dishes || []).filter(d => d.a_fiche).length;

  // ── Carte functions ──
  const handleScanCarte = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setScanLoading(true);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));
      const res = await api.post('/ai/scan-carte-multi', formData, { timeout: 120000 });
      const { total_detectes = 0, nouveaux = 0 } = res.data;
      if (total_detectes > 0) {
        toast.success(`${total_detectes} plats detectes, ${nouveaux} nouveaux ajoutes`);
      } else {
        toast.info('Aucun plat detecte dans le document');
      }
      onRefresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erreur scan carte');
    } finally { setScanLoading(false); e.target.value = ''; }
  };

  const addItem = async () => {
    if (!newItem.nom || !newItem.prix_ttc) return;
    try {
      await api.post('/carte-items', {
        nom: newItem.nom, prix_ttc: parseFloat(newItem.prix_ttc),
        cout_matiere: parseFloat(newItem.cout_matiere) || 0,
        section: newItem.section, categorie: newItem.categorie,
        description: newItem.description, disponible: true,
      });
      toast.success('Plat ajoute');
      setNewItem({ nom: '', prix_ttc: '', cout_matiere: '', section: 'Entrées', categorie: 'entree', description: '' });
      setAddingItem(false);
      onRefresh();
    } catch { toast.error('Erreur ajout'); }
  };

  const deleteItem = async (id) => {
    try {
      await api.delete(`/carte-items/${id}`);
      toast.success('Supprime');
      onRefresh();
    } catch { toast.error('Erreur'); }
  };

  const toggleItem = async (id) => {
    try {
      await api.patch(`/carte-items/${id}/toggle`);
      onRefresh();
    } catch { toast.error('Erreur'); }
  };

  const startEdit = (item) => {
    setEditId(item.id);
    setEditData({ nom: item.nom, prix_ttc: item.prix_ttc, description: item.description || '' });
  };

  const saveEdit = async (item) => {
    try {
      await api.post('/carte-items', { ...item, ...editData, prix_ttc: parseFloat(editData.prix_ttc) || item.prix_ttc });
      toast.success('Modifie');
      setEditId(null);
      onRefresh();
    } catch { toast.error('Erreur'); }
  };

  const SECTION_CAT_MAP = {
    'Entrées': 'entree', 'Entrees': 'entree', 'Tapas': 'tapas',
    'Viandes': 'plat_viande', 'Plats Viande': 'plat_viande',
    'Poissons': 'plat_poisson', 'Plats Poisson': 'plat_poisson',
    'Desserts': 'dessert', 'Vins Blancs': 'vin_blanc', 'Vins Rouges': 'vin_rouge',
    'Vins Roses': 'vin_rose', 'Vins Rosés': 'vin_rose',
  };

  // ── Fiche functions ──
  const editCout = useMemo(() => {
    if (!editDish?.ingredients) return 0;
    return editDish.ingredients.reduce((s, i) => {
      const cout = i.cout || 0;
      const q = i.quantite || 0;
      const u = (i.unite || '').toLowerCase();
      if (['g', 'gr'].includes(u)) return s + cout * q / 1000;
      if (u === 'ml') return s + cout * q / 1000;
      if (u === 'cl') return s + cout * q / 100;
      return s + cout * q;
    }, 0);
  }, [editDish?.ingredients]);

  const editPrixHt = editDish ? (editDish.prix_ht || editDish.prix_ttc / 1.1) : 0;
  const editRatio = editPrixHt > 0 ? (editCout / editPrixHt) * 100 : 0;
  const editMarge = editPrixHt - editCout;

  const searchMercuriale = async (idx, query) => {
    updateIngredient(idx, 'nom', query);
    setAcIdx(idx);
    if (query.length < 2) { setAcResults([]); return; }
    try {
      const r = await api.get(`/marges/search-ingredients?q=${encodeURIComponent(query)}`);
      setAcResults(r.data || []);
    } catch { setAcResults([]); }
  };

  const selectMercuriale = (idx, result) => {
    setEditDish(prev => ({
      ...prev,
      ingredients: prev.ingredients.map((ing, i) => i === idx ? {
        ...ing, nom: result.nom, cout: result.cout || 0,
        unite: result.unite || ing.unite, fournisseur: result.fournisseur || '',
      } : ing),
    }));
    setAcResults([]);
    setAcIdx(null);
    toast.success(`Prix mis a jour : ${result.cout?.toFixed(2)}/${result.unite}`);
  };

  const addIngredient = () => {
    setEditDish(prev => ({
      ...prev,
      ingredients: [...(prev.ingredients || []), { nom: '', quantite: 0, unite: 'kg', cout: 0, fournisseur: '' }],
    }));
  };

  const updateIngredient = (idx, field, value) => {
    setEditDish(prev => ({
      ...prev,
      ingredients: prev.ingredients.map((ing, i) => i === idx ? { ...ing, [field]: value } : ing),
    }));
  };

  const removeIngredient = (idx) => {
    setEditDish(prev => ({ ...prev, ingredients: prev.ingredients.filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    if (!editDish) return;
    try {
      await api.put(`/marges/fiche/${editDish.id}`, { ingredients: editDish.ingredients || [] });
      toast.success('Fiche technique sauvegardee');
      setEditDish(null);
      onRefresh();
    } catch { toast.error('Erreur sauvegarde'); }
  };

  const handleAiSuggest = async () => {
    if (!editDish) return;
    setAiLoading(true);
    try {
      const res = await api.post('/marges/ai-suggest', {
        nom: editDish.nom, categorie: editDish.categorie, prix_ttc: editDish.prix_ttc,
      });
      const ings = res.data.ingredients || [];
      setEditDish(prev => ({
        ...prev, ingredients: ings,
        ai_conseil: res.data.conseil || '', ai_prix_conseille: res.data.prix_vente_conseille_ttc || null,
      }));
      toast.success(`${ings.length} ingredients suggeres par Claude`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur IA');
    }
    setAiLoading(false);
  };

  const openPrompt = async () => {
    try {
      const res = await fetch('/prompt-claude.md');
      const text = await res.text();
      const promptStart = text.indexOf('Tu es le chef');
      setPromptText(promptStart >= 0 ? text.substring(promptStart) : text);
      setShowPrompt(true);
    } catch { toast.error('Erreur chargement du prompt'); }
  };

  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(promptText);
      toast.success('Prompt copie !');
    } catch {
      const ta = document.getElementById('prompt-textarea');
      if (ta) { ta.select(); document.execCommand('copy'); toast.success('Prompt copie !'); }
    }
  };

  const handleImport = async () => {
    if (!importJson.trim()) { toast.error('Collez le JSON de Claude'); return; }
    setImporting(true);
    setImportResult(null);
    try {
      let parsed = importJson.trim();
      if (parsed.startsWith('```')) {
        parsed = parsed.replace(/^```(?:json)?\n?/, '').replace(/\n?```$/, '');
      }
      const data = JSON.parse(parsed);
      const res = await api.post('/marges/import-claude', data);
      setImportResult(res.data);
      if (res.data.imported > 0) { toast.success(`${res.data.imported} plats importes !`); onRefresh(); }
      if (res.data.skipped > 0) { toast.warning(`${res.data.skipped} plats ignores`); }
    } catch (err) {
      if (err instanceof SyntaxError) { toast.error('JSON invalide'); }
      else { toast.error(err.response?.data?.detail || 'Erreur import'); }
    }
    setImporting(false);
  };

  const openFiche = (item) => {
    const dish = dishMap[item.nom?.toLowerCase()];
    if (dish) {
      setEditDish({ ...dish });
    } else {
      setEditDish({
        id: item.id, nom: item.nom, prix_ttc: item.prix_ttc,
        categorie: item.categorie, section: item.section,
        ingredients: [], a_fiche: false,
      });
    }
  };

  return (
    <div className="space-y-4 mt-4" data-testid="carte-tab">
      {/* KPI */}
      <div className="grid grid-cols-3 gap-3">
        <div className="kpi-card text-center">
          <p className="kpi-label">Total</p>
          <p className="font-mono text-xl text-[rgb(var(--lm-text))]">{items.length}</p>
        </div>
        <div className="kpi-card text-center border-[rgb(var(--lm-green))]/30">
          <p className="kpi-label">Avec fiche</p>
          <p className="font-mono text-xl text-[rgb(var(--lm-green))]">{withFiche}</p>
        </div>
        <div className="kpi-card text-center border-orange-400/30">
          <p className="kpi-label">Sans fiche</p>
          <p className="font-mono text-xl text-orange-400">{(dishes || []).length - withFiche}</p>
        </div>
      </div>

      <CategoryFilter value={catFilter} onChange={setCatFilter} counts={counts} />

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[rgb(var(--lm-text-muted))]" />
          <Input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher..." className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] pl-9 text-sm h-9" data-testid="carte-search" />
        </div>
        <Button onClick={() => setAddingItem(!addingItem)} size="sm" className="bg-lemas-gold text-[rgb(var(--lm-bg))] h-9" data-testid="add-item-btn">
          <Plus size={14} className="mr-1" /> Ajouter
        </Button>
        <label className="cursor-pointer">
          <input type="file" accept="image/*,.pdf,application/pdf" multiple className="hidden" onChange={handleScanCarte} data-testid="scan-carte-input" />
          <Button asChild size="sm" variant="outline" className="border-lemas-gold/30 text-lemas-gold h-9" disabled={scanLoading}>
            <span>{scanLoading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Camera size={14} className="mr-1" />} Scanner</span>
          </Button>
        </label>
      </div>

      {/* Claude Import Buttons */}
      <div className="flex gap-2" data-testid="claude-import-section">
        <Button onClick={openPrompt} variant="outline" size="sm"
          className="flex-1 h-9 text-xs border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text-secondary))] hover:border-lemas-gold/40"
          data-testid="copy-prompt-btn">
          <Copy size={13} className="mr-1.5" /> Prompt Claude
        </Button>
        <Button onClick={() => { setShowImport(true); setImportResult(null); setImportJson(''); }} size="sm"
          className="flex-1 h-9 text-xs bg-lemas-gold/10 border border-lemas-gold/30 text-lemas-gold hover:bg-lemas-gold/20"
          data-testid="import-claude-btn">
          <ClipboardPaste size={13} className="mr-1.5" /> Import Claude
        </Button>
      </div>

      {/* Add form */}
      {addingItem && (
        <div className="kpi-card border-lemas-gold/30 space-y-3 animate-slide-up" data-testid="add-form">
          <div className="grid grid-cols-2 gap-3">
            <Input value={newItem.nom} onChange={e => setNewItem({...newItem, nom: e.target.value})} placeholder="Nom du plat" className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" data-testid="new-item-nom" />
            <select value={newItem.section} onChange={e => setNewItem({...newItem, section: e.target.value, categorie: SECTION_CAT_MAP[e.target.value] || 'entree'})}
              className="bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] rounded px-3 text-sm text-[rgb(var(--lm-text))]">
              {Object.keys(SECTION_CAT_MAP).filter((v, i, a) => a.indexOf(v) === i).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-[rgb(var(--lm-text-secondary))]">Prix TTC</label>
              <Input type="number" value={newItem.prix_ttc} onChange={e => setNewItem({...newItem, prix_ttc: e.target.value})}
                className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono" placeholder="0" data-testid="new-item-prix" />
            </div>
            <div>
              <label className="text-xs text-[rgb(var(--lm-text-secondary))]">Description</label>
              <Input value={newItem.description} onChange={e => setNewItem({...newItem, description: e.target.value})}
                className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" placeholder="Optionnel" />
            </div>
            <div className="flex items-end">
              <Button onClick={addItem} className="w-full bg-lemas-gold text-[rgb(var(--lm-bg))]" data-testid="save-new-item"><Check size={14} /></Button>
            </div>
          </div>
        </div>
      )}

      {/* Dishes list */}
      {Object.keys(grouped).length === 0 ? (
        <div className="py-12 text-center kpi-card">
          <UtensilsCrossed size={32} className="mx-auto text-[rgb(var(--lm-text-muted))] mb-3" />
          <p className="text-[rgb(var(--lm-text-secondary))]">Aucun plat</p>
        </div>
      ) : Object.entries(grouped).map(([section, sectionItems]) => (
        <div key={section} className="space-y-1">
          <h3 className="font-title text-base text-lemas-gold mt-2 mb-1">{section}</h3>
          {sectionItems.map(item => {
            const dish = dishMap[item.nom?.toLowerCase()];
            const ratio = dish?.ratio_matiere || 0;
            const status = dish ? ratioStatus(ratio, dish.categorie || item.categorie) : 'none';
            const hasFiche = dish?.a_fiche;
            return (
              <div key={item.id} className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border transition-all ${
                !item.disponible ? 'opacity-50 border-[rgb(var(--lm-border))] bg-[rgb(var(--lm-surface))]/50' : 'border-[rgb(var(--lm-border))] bg-[rgb(var(--lm-surface))]'
              }`} data-testid={`carte-item-${item.id}`}>
                {editId === item.id ? (
                  <>
                    <Input value={editData.nom} onChange={e => setEditData({...editData, nom: e.target.value})}
                      className="h-7 text-sm flex-1 bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" />
                    <Input type="number" value={editData.prix_ttc} onChange={e => setEditData({...editData, prix_ttc: e.target.value})}
                      className="h-7 text-sm w-20 font-mono bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] text-center" />
                    <button onClick={() => saveEdit(item)} className="text-[rgb(var(--lm-green))]"><Check size={14} /></button>
                    <button onClick={() => setEditId(null)} className="text-[rgb(var(--lm-text-muted))]"><X size={14} /></button>
                  </>
                ) : (
                  <>
                    <button onClick={() => openFiche(item)} className="flex-1 min-w-0 text-left" data-testid={`fiche-btn-${item.id}`}>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full shrink-0 ${hasFiche ? 'bg-[rgb(var(--lm-green))]' : 'bg-[rgb(var(--lm-border))]'}`} />
                        <p className="text-sm font-medium truncate text-[rgb(var(--lm-text))]">{item.nom}</p>
                      </div>
                      {item.description && <p className="text-xs text-[rgb(var(--lm-text-muted))] truncate ml-4">{item.description}</p>}
                    </button>
                    <span className="font-mono text-sm text-lemas-gold whitespace-nowrap">{formatCurrency(item.prix_ttc)}</span>
                    {ratio > 0 ? (
                      <span className={`font-mono text-xs w-10 text-right ${statusColor(status)}`}>{ratio.toFixed(0)}%</span>
                    ) : (
                      <span className="text-xs text-[rgb(var(--lm-text-muted))] w-10 text-right">--</span>
                    )}
                    <Switch checked={item.disponible !== false} onCheckedChange={() => toggleItem(item.id)} data-testid={`toggle-${item.id}`} />
                    <button onClick={() => startEdit(item)} className="text-[rgb(var(--lm-text-muted))] hover:text-lemas-gold"><Edit3 size={13} /></button>
                    <button onClick={() => deleteItem(item.id)} className="text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-red))]"><Trash2 size={13} /></button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {/* ── Fiche Editor Dialog ── */}
      <Dialog open={!!editDish} onOpenChange={(open) => { if (!open) { setEditDish(null); setAcResults([]); setAcIdx(null); } }}>
        <DialogContent className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))] max-w-lg max-h-[90vh] overflow-y-auto p-0">
          {editDish && (
            <>
              <div className="sticky top-0 z-10 bg-[rgb(var(--lm-surface))] border-b border-[rgb(var(--lm-border))] p-4">
                <DialogHeader>
                  <DialogTitle className="font-title text-lg flex items-center gap-2">
                    <ChefHat size={18} className="text-lemas-gold" /> {editDish.nom}
                  </DialogTitle>
                </DialogHeader>
              </div>
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-3 gap-2">
                  <div className="p-3 rounded-lg bg-lemas-gold/10 text-center">
                    <p className="text-[9px] text-lemas-gold uppercase">Prix TTC</p>
                    <p className="font-mono text-lg text-lemas-gold font-bold">{editDish.prix_ttc?.toFixed(2)}EUR</p>
                  </div>
                  <div className="p-3 rounded-lg bg-[rgb(var(--lm-bg))] text-center">
                    <p className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase">Prix HT</p>
                    <p className="font-mono text-lg text-[rgb(var(--lm-text))]">{editPrixHt.toFixed(2)}EUR</p>
                  </div>
                  <div className="p-3 rounded-lg bg-[rgb(var(--lm-bg))] text-center">
                    <p className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase">Cible</p>
                    <p className="font-mono text-lg text-[rgb(var(--lm-text-secondary))]">{getTarget(editDish.categorie).min}-{getTarget(editDish.categorie).max}%</p>
                  </div>
                </div>

                <div className={`rounded-lg p-3 ${editCout > 0 ? (editRatio <= getTarget(editDish.categorie).max ? 'bg-[rgb(var(--lm-green))]/10' : editRatio <= getTarget(editDish.categorie).max + 5 ? 'bg-orange-400/10' : 'bg-red-400/10') : 'bg-[rgb(var(--lm-bg))]'}`}>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase">Cout matiere</p>
                      <p className="font-mono text-xl text-[rgb(var(--lm-text))] font-bold">{editCout.toFixed(2)}EUR</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase">Food cost</p>
                      <p className={`font-mono text-xl font-bold ${statusColor(ratioStatus(editRatio, editDish.categorie))}`}>
                        {editCout > 0 ? `${editRatio.toFixed(1)}%` : '--'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase">Marge brute</p>
                      <p className={`font-mono text-lg ${editMarge >= 0 ? 'text-[rgb(var(--lm-green))]' : 'text-[rgb(var(--lm-red))]'}`}>
                        {editCout > 0 ? `${editMarge.toFixed(2)}EUR` : '--'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase">Marge %</p>
                      <p className="font-mono text-lg text-[rgb(var(--lm-text))]">
                        {editCout > 0 ? `${(100 - editRatio).toFixed(1)}%` : '--'}
                      </p>
                    </div>
                  </div>
                  {editCout > 0 && (
                    <div className="w-full h-2 bg-white/10 rounded-full mt-3 overflow-hidden relative">
                      <div className={`h-full rounded-full transition-all ${statusBarBg(ratioStatus(editRatio, editDish.categorie))}`}
                        style={{ width: `${Math.min(editRatio * 2, 100)}%` }} />
                      <div className="absolute top-0 h-full w-0.5 bg-white/40" style={{ left: `${getTarget(editDish.categorie).max * 2}%` }} />
                    </div>
                  )}
                </div>

                <Button onClick={handleAiSuggest} disabled={aiLoading}
                  className="w-full bg-lemas-gold/10 border border-lemas-gold/30 text-lemas-gold hover:bg-lemas-gold/20 py-3" data-testid="ai-suggest-btn">
                  <Sparkles size={14} className={`mr-2 ${aiLoading ? 'animate-spin' : ''}`} />
                  {aiLoading ? 'Claude analyse le plat...' : 'Detailler avec Claude IA'}
                </Button>

                {editDish.ai_conseil && (
                  <div className="bg-lemas-gold/5 rounded-lg p-3 border border-lemas-gold/20 space-y-1" data-testid="ai-conseil">
                    <p className="text-[9px] text-lemas-gold uppercase font-bold">Conseil du Chef IA</p>
                    <p className="text-xs text-[rgb(var(--lm-text-secondary))] italic">{editDish.ai_conseil}</p>
                  </div>
                )}

                <div className="space-y-2">
                  <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase flex items-center gap-1">
                    <Package size={10} /> Ingredients ({editDish.ingredients?.length || 0})
                  </p>
                  {(editDish.ingredients || []).map((ing, i) => (
                    <div key={i} className="rounded-lg bg-[rgb(var(--lm-bg))]/50 p-2.5 space-y-1.5" data-testid={`fiche-ingredient-${i}`}>
                      <div className="flex items-center justify-between">
                        <div className="relative flex-1 mr-2">
                          <Search size={10} className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[rgb(var(--lm-text-muted))]" />
                          <Input value={ing.nom}
                            onChange={e => searchMercuriale(i, e.target.value)}
                            onFocus={() => { if (ing.nom?.length >= 2) searchMercuriale(i, ing.nom); }}
                            onBlur={() => setTimeout(() => { if (acIdx === i) { setAcResults([]); setAcIdx(null); } }, 250)}
                            className="bg-transparent border-none text-sm h-7 font-medium pl-6 focus-visible:ring-1 focus-visible:ring-lemas-gold/30"
                            placeholder="Chercher mercuriale..." data-testid={`fiche-ing-name-${i}`} />
                          {acIdx === i && acResults.length > 0 && (
                            <div className="absolute z-30 left-0 right-0 mt-0.5 bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))] rounded-lg shadow-lg max-h-40 overflow-y-auto">
                              {acResults.map((r, ri) => (
                                <button key={ri} onClick={() => selectMercuriale(i, r)}
                                  className="w-full text-left px-3 py-2 hover:bg-lemas-gold/10 text-xs flex justify-between items-center border-b border-[rgb(var(--lm-border))]/50 last:border-0"
                                  data-testid={`fiche-ac-result-${ri}`}>
                                  <span className="truncate flex-1">{r.nom}</span>
                                  <span className="flex items-center gap-2 shrink-0 ml-2">
                                    <span className="font-mono text-lemas-gold font-bold">{r.cout?.toFixed(2)}/{r.unite}</span>
                                    {r.fournisseur && <span className="text-[9px] text-[rgb(var(--lm-text-muted))]">{r.fournisseur}</span>}
                                  </span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="font-mono text-xs text-[rgb(var(--lm-green))]">{(() => {
                            const c = ing.cout || 0, q = ing.quantite || 0, u = (ing.unite || '').toLowerCase();
                            if (['g','gr'].includes(u)) return (c * q / 1000).toFixed(2);
                            if (u === 'ml') return (c * q / 1000).toFixed(2);
                            if (u === 'cl') return (c * q / 100).toFixed(2);
                            return (c * q).toFixed(2);
                          })()}EUR</span>
                          <button onClick={() => removeIngredient(i)} className="text-red-400/40 hover:text-red-400"><Trash2 size={12} /></button>
                        </div>
                      </div>
                      {ing.fournisseur && <p className="text-[9px] text-lemas-gold/60 ml-6">{ing.fournisseur}</p>}
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <label className="text-[8px] text-[rgb(var(--lm-text-muted))] uppercase">Qte</label>
                          <Input type="number" step="0.001" value={ing.quantite || ''} onChange={e => updateIngredient(i, 'quantite', parseFloat(e.target.value) || 0)}
                            className="bg-transparent border-[rgb(var(--lm-border))] text-xs h-7 font-mono text-center" />
                        </div>
                        <div className="w-14">
                          <label className="text-[8px] text-[rgb(var(--lm-text-muted))] uppercase">Unite</label>
                          <select value={ing.unite || 'kg'} onChange={e => updateIngredient(i, 'unite', e.target.value)}
                            className="h-7 w-full text-xs bg-transparent border border-[rgb(var(--lm-border))] rounded px-1 text-[rgb(var(--lm-text))]">
                            {['kg', 'g', 'L', 'cl', 'ml', 'piece', 'tranche', 'botte'].map(u => <option key={u} value={u}>{u}</option>)}
                          </select>
                        </div>
                        <div className="flex-1">
                          <label className="text-[8px] text-[rgb(var(--lm-text-muted))] uppercase">Prix/u</label>
                          <Input type="number" step="0.01" value={ing.cout || ''} onChange={e => updateIngredient(i, 'cout', parseFloat(e.target.value) || 0)}
                            className="bg-transparent border-[rgb(var(--lm-border))] text-xs h-7 font-mono text-center" />
                        </div>
                      </div>
                    </div>
                  ))}

                  <Button size="sm" onClick={addIngredient} variant="outline"
                    className="w-full h-9 text-xs border-dashed border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text-secondary))] hover:border-lemas-gold/40"
                    data-testid="add-ingredient-btn">
                    <Plus size={12} className="mr-1" /> Ajouter un ingredient
                  </Button>
                </div>

                {editCout > 0 && (() => {
                  const target = getTarget(editDish.categorie);
                  const prixMin = Math.round((editCout / target.max * 100) * 1.1);
                  const prixMax = Math.round((editCout / target.min * 100) * 1.1);
                  return (
                    <div className="rounded-lg p-3 bg-lemas-gold/5 border border-lemas-gold/20" data-testid="prix-conseille">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-[9px] text-lemas-gold uppercase">Prix TTC conseille</p>
                          <p className="font-mono text-xl text-lemas-gold font-bold">{prixMin} - {prixMax} EUR</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase">Cible</p>
                          <p className="text-sm text-[rgb(var(--lm-text-secondary))]">{target.min}-{target.max}%</p>
                        </div>
                      </div>
                      {editDish.prix_ttc > 0 && (
                        <p className="text-[10px] mt-2 text-[rgb(var(--lm-text-muted))]">
                          Prix actuel : {editDish.prix_ttc}EUR
                          {editDish.prix_ttc < prixMin && <span className="text-red-400 font-bold ml-1">(trop bas !)</span>}
                          {editDish.prix_ttc > prixMax && <span className="text-[rgb(var(--lm-green))] ml-1">(bonne marge)</span>}
                          {editDish.prix_ttc >= prixMin && editDish.prix_ttc <= prixMax && <span className="text-lemas-gold ml-1">(dans la cible)</span>}
                        </p>
                      )}
                    </div>
                  );
                })()}

                <Button onClick={handleSave} className="w-full bg-lemas-gold text-[rgb(var(--lm-bg))] hover:bg-lemas-gold/90 py-3" data-testid="save-fiche-btn">
                  <Save size={14} className="mr-2" /> Enregistrer la fiche
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Prompt Dialog */}
      <Dialog open={showPrompt} onOpenChange={setShowPrompt}>
        <DialogContent className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[rgb(var(--lm-text))] flex items-center gap-2">
              <Copy size={18} className="text-lemas-gold" /> Prompt pour Claude
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <textarea id="prompt-textarea" readOnly value={promptText} onFocus={e => e.target.select()}
              className="w-full h-64 p-3 rounded-lg bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))] text-[10px] font-mono text-[rgb(var(--lm-text))] resize-none" data-testid="prompt-textarea" />
            <Button onClick={doCopy} className="w-full bg-lemas-gold text-[rgb(var(--lm-bg))] hover:bg-lemas-gold/90 py-3" data-testid="copy-prompt-submit-btn">
              <Copy size={14} className="mr-2" /> Copier le prompt
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[rgb(var(--lm-text))] flex items-center gap-2">
              <ClipboardPaste size={18} className="text-lemas-gold" /> Importer depuis Claude
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <textarea value={importJson} onChange={e => setImportJson(e.target.value)}
              placeholder='{"plats": [{"nom": "...", "ingredients": [...]}]}'
              className="w-full h-48 p-3 rounded-lg bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))] text-xs font-mono text-[rgb(var(--lm-text))] resize-none" data-testid="import-json-input" />
            <Button onClick={handleImport} disabled={importing || !importJson.trim()}
              className="w-full bg-lemas-gold text-[rgb(var(--lm-bg))] hover:bg-lemas-gold/90 py-3" data-testid="import-submit-btn">
              {importing ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Upload size={14} className="mr-2" />}
              {importing ? 'Import en cours...' : 'Importer les fiches'}
            </Button>
            {importResult && (
              <div className="space-y-2 pt-2" data-testid="import-results">
                <div className="flex gap-2">
                  <div className="flex-1 rounded-lg p-2 bg-[rgb(var(--lm-green))]/10 text-center">
                    <p className="font-mono text-xl text-[rgb(var(--lm-green))]">{importResult.imported}</p>
                    <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">importes</p>
                  </div>
                  {importResult.skipped > 0 && (
                    <div className="flex-1 rounded-lg p-2 bg-orange-400/10 text-center">
                      <p className="font-mono text-xl text-orange-400">{importResult.skipped}</p>
                      <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">ignores</p>
                    </div>
                  )}
                </div>
                {importResult.details?.map((d, i) => (
                  <div key={i} className="flex items-center justify-between px-2 py-1.5 rounded bg-[rgb(var(--lm-surface))] text-xs">
                    <span className="text-[rgb(var(--lm-text))] truncate flex-1">{d.nom}</span>
                    <span className={`font-mono font-bold mx-2 ${d.food_cost <= 33 ? 'text-[rgb(var(--lm-green))]' : 'text-orange-400'}`}>{d.food_cost}%</span>
                    <span className="text-lemas-gold font-mono">{d.prix_conseille}EUR</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════
// TAB: MARGE ─ Simplified margin overview
// ══════════════════════════════════════════════════════════════════
const MargeTab = ({ dishes, kpis, parCategorie, catFilter, setCatFilter, onRefresh }) => {
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  const filtered = useMemo(() => {
    let list = dishes;
    if (catFilter !== 'all') {
      const grp = CATEGORY_GROUPS.find(g => g.id === catFilter);
      if (grp?.cats) list = list.filter(d => grp.cats.includes(d.categorie));
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(d => d.nom.toLowerCase().includes(q));
    }
    // Sort: flagged first (ratio > 0 and over target), then by ratio desc
    return [...list].sort((a, b) => {
      if (a.cout_matiere > 0 && b.cout_matiere <= 0) return -1;
      if (a.cout_matiere <= 0 && b.cout_matiere > 0) return 1;
      return b.ratio_matiere - a.ratio_matiere;
    });
  }, [dishes, catFilter, search]);

  const counts = useMemo(() => {
    const c = {};
    CATEGORY_GROUPS.forEach(g => {
      if (g.id !== 'all') c[g.id] = g.cats ? dishes.filter(d => g.cats.includes(d.categorie)).length : 0;
    });
    return c;
  }, [dishes]);

  const withCost = filtered.filter(d => d.cout_matiere > 0);
  const avgFoodCost = withCost.length > 0 ? withCost.reduce((s, d) => s + d.ratio_matiere, 0) / withCost.length : 0;

  const handleBulkFill = async () => {
    const unfilled = dishes.filter(d => !d.ingredients?.length && ['entree','tapas','plat_viande','plat_poisson','dessert'].includes(d.categorie));
    if (unfilled.length === 0) { toast.info('Tous les plats ont deja une fiche'); return; }
    setBulkLoading(true);
    toast.info(`Remplissage IA de ${unfilled.length} plats...`);
    try {
      await api.post('/marges/ai-bulk-fill', {});
      const poll = setInterval(async () => {
        try {
          const res = await api.get('/marges/ai-bulk-fill/status');
          const { status, filled, total } = res.data;
          if (status === 'done') {
            clearInterval(poll);
            setBulkLoading(false);
            toast.success(`${filled}/${total} plats remplis`);
            onRefresh();
          } else if (status === 'running') {
            toast.info(`${filled}/${total}...`, { id: 'bulk-progress' });
          }
        } catch { /* ignore */ }
      }, 5000);
    } catch (err) {
      setBulkLoading(false);
      toast.error(err.response?.data?.detail || 'Erreur IA');
    }
  };

  const unfilledCount = dishes.filter(d => !d.ingredients?.length && ['entree','tapas','plat_viande','plat_poisson','dessert'].includes(d.categorie)).length;

  return (
    <div className="space-y-4 mt-4" data-testid="marge-tab">
      {/* Simple summary bar */}
      <div className="kpi-card flex items-center justify-between" data-testid="kpi-food-cost">
        <div>
          <p className="text-xs text-[rgb(var(--lm-text-muted))] uppercase">Food Cost Moyen</p>
          <p className={`font-mono text-3xl font-bold ${statusColor(avgFoodCost > 0 ? (avgFoodCost <= 30 ? 'green' : avgFoodCost <= 35 ? 'orange' : 'red') : 'none')}`}>
            {avgFoodCost > 0 ? `${avgFoodCost.toFixed(1)}%` : '--'}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-[rgb(var(--lm-text-muted))]">{withCost.length} plats analyses</p>
          <p className="text-xs text-[rgb(var(--lm-text-muted))]">sur {filtered.length} total</p>
          {withCost.length > 0 && (
            <div className="flex gap-2 mt-1 justify-end">
              <span className="flex items-center gap-1 text-[10px]">
                <span className="w-1.5 h-1.5 rounded-full bg-[rgb(var(--lm-green))]" />
                {withCost.filter(d => ratioStatus(d.ratio_matiere, d.categorie) === 'green').length}
              </span>
              <span className="flex items-center gap-1 text-[10px]">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-400" />
                {withCost.filter(d => ratioStatus(d.ratio_matiere, d.categorie) === 'orange').length}
              </span>
              <span className="flex items-center gap-1 text-[10px]">
                <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                {withCost.filter(d => ratioStatus(d.ratio_matiere, d.categorie) === 'red').length}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Bulk fill */}
      {unfilledCount > 0 && (
        <Button onClick={handleBulkFill} disabled={bulkLoading}
          className="w-full bg-lemas-gold/10 border border-lemas-gold/30 text-lemas-gold hover:bg-lemas-gold/20" data-testid="bulk-fill-btn">
          {bulkLoading ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Sparkles size={14} className="mr-2" />}
          {bulkLoading ? 'IA en cours...' : `Remplir ${unfilledCount} plats par IA`}
        </Button>
      )}

      <CategoryFilter value={catFilter} onChange={setCatFilter} counts={counts} />

      <div className="relative max-w-xs">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[rgb(var(--lm-text-muted))]" />
        <Input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Rechercher..." className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] pl-9 text-sm h-9" data-testid="marge-search" />
      </div>

      {/* Dishes — clean list */}
      <div className="space-y-1" data-testid="marge-table">
        {filtered.length === 0 ? (
          <div className="py-12 text-center kpi-card">
            <PieChart size={32} className="mx-auto text-[rgb(var(--lm-text-muted))] mb-3" />
            <p className="text-[rgb(var(--lm-text-secondary))]">Aucun plat</p>
          </div>
        ) : filtered.map(dish => {
          const status = ratioStatus(dish.ratio_matiere, dish.categorie);
          const target = getTarget(dish.categorie);
          const isExpanded = expandedId === dish.id;

          return (
            <div key={dish.id} data-testid={`marge-row-${dish.id}`}>
              <button onClick={() => setExpandedId(isExpanded ? null : dish.id)}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg border text-left transition-all ${
                  isExpanded ? 'border-lemas-gold/40 bg-[rgb(var(--lm-surface))]' : 'border-[rgb(var(--lm-border))] bg-[rgb(var(--lm-surface))] hover:bg-[rgb(var(--lm-surface-elevated))]'
                }`}>
                {/* Status dot */}
                <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${dish.cout_matiere > 0 ? statusBarBg(status) : 'bg-[rgb(var(--lm-border))]'}`} />
                {/* Name */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate text-[rgb(var(--lm-text))]">{dish.nom}</p>
                </div>
                {/* Price */}
                <span className="font-mono text-sm text-lemas-gold whitespace-nowrap">{formatCurrency(dish.prix_ttc)}</span>
                {/* Food cost % */}
                {dish.cout_matiere > 0 ? (
                  <span className={`font-mono text-sm font-bold w-14 text-right ${statusColor(status)}`}>
                    {dish.ratio_matiere.toFixed(0)}%
                  </span>
                ) : (
                  <span className="text-xs text-[rgb(var(--lm-text-muted))] w-14 text-right">--</span>
                )}
              </button>

              {/* Expanded detail — simple read-only view */}
              {isExpanded && (
                <div className="ml-5 mr-1 mt-1 mb-2 p-3 rounded-lg bg-[rgb(var(--lm-bg))]/50 border border-[rgb(var(--lm-border))] space-y-3 animate-slide-up" data-testid={`marge-detail-${dish.id}`}>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase">Cout matiere</p>
                      <p className="font-mono text-lg text-[rgb(var(--lm-text))]">{dish.cout_matiere > 0 ? `${dish.cout_matiere.toFixed(2)}` : '--'}</p>
                    </div>
                    <div>
                      <p className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase">Food cost</p>
                      <p className={`font-mono text-lg font-bold ${statusColor(status)}`}>
                        {dish.cout_matiere > 0 ? `${dish.ratio_matiere.toFixed(1)}%` : '--'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase">Marge</p>
                      <p className="font-mono text-lg text-[rgb(var(--lm-green))]">
                        {dish.cout_matiere > 0 ? `${dish.marge_brute.toFixed(2)}` : '--'}
                      </p>
                    </div>
                  </div>
                  <div className="text-[10px] text-[rgb(var(--lm-text-muted))]">
                    Cible {target.min}-{target.max}% · {dish.ingredients?.length || 0} ingredients
                  </div>
                  {dish.ingredients?.length > 0 && (
                    <div className="space-y-0.5">
                      {dish.ingredients.map((ing, i) => (
                        <div key={i} className="flex justify-between text-xs px-1">
                          <span className="text-[rgb(var(--lm-text-secondary))] truncate">{ing.nom}</span>
                          <span className="font-mono text-[rgb(var(--lm-text-muted))] shrink-0 ml-2">
                            {ing.quantite}{ing.unite}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-[10px] text-[rgb(var(--lm-text-muted))] italic">
                    Pour modifier les ingredients, utilisez l'onglet Fiche
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};


// ══════════════════════════════════════════════════════════════════
// TAB: RECETTES ─ Sub-recipes (sauces, bases, garnitures)
// ══════════════════════════════════════════════════════════════════
const RECETTE_TYPES = [
  { id: 'sauce', label: 'Sauces' },
  { id: 'base', label: 'Bases' },
  { id: 'garniture', label: 'Garnitures' },
  { id: 'marinade', label: 'Marinades' },
  { id: 'dessert', label: 'Desserts' },
  { id: 'autre', label: 'Autre' },
];

const RecettesTab = ({ recettes, onRefresh }) => {
  const [editRecette, setEditRecette] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [acIdx, setAcIdx] = useState(null);
  const [acResults, setAcResults] = useState([]);

  const newRecette = () => ({
    nom: '', type: 'sauce', ingredients: [{ nom: '', quantite: 0, unite: 'kg', cout: 0 }],
    nb_portions: 10, unite_portion: 'portion', instructions: '',
  });

  const openEdit = (r) => { setEditRecette({...r}); setShowNew(false); };
  const openNew = () => { setEditRecette(newRecette()); setShowNew(true); };

  const updateIng = (idx, field, value) => {
    setEditRecette(prev => ({
      ...prev,
      ingredients: prev.ingredients.map((ing, i) => i === idx ? { ...ing, [field]: value } : ing),
    }));
  };
  const addIng = () => {
    setEditRecette(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, { nom: '', quantite: 0, unite: 'kg', cout: 0 }],
    }));
  };
  const removeIng = (idx) => {
    setEditRecette(prev => ({ ...prev, ingredients: prev.ingredients.filter((_, i) => i !== idx) }));
  };

  const searchMercuriale = async (idx, query) => {
    updateIng(idx, 'nom', query);
    setAcIdx(idx);
    if (query.length < 2) { setAcResults([]); return; }
    try {
      const r = await api.get(`/marges/search-ingredients?q=${encodeURIComponent(query)}`);
      setAcResults(r.data || []);
    } catch { setAcResults([]); }
  };

  const selectAc = (idx, result) => {
    setEditRecette(prev => ({
      ...prev,
      ingredients: prev.ingredients.map((ing, i) => i === idx ? {
        ...ing, nom: result.nom, cout: result.cout || 0, unite: result.unite || ing.unite,
        fournisseur: result.fournisseur || '',
      } : ing),
    }));
    setAcResults([]); setAcIdx(null);
  };

  const editCout = useMemo(() => {
    if (!editRecette?.ingredients) return 0;
    return editRecette.ingredients.reduce((s, i) => {
      const c = i.cout || 0, q = i.quantite || 0, u = (i.unite || '').toLowerCase();
      if (['g','gr'].includes(u)) return s + c * q / 1000;
      if (u === 'ml') return s + c * q / 1000;
      if (u === 'cl') return s + c * q / 100;
      return s + c * q;
    }, 0);
  }, [editRecette?.ingredients]);

  const coutPortion = editRecette?.nb_portions > 0 ? editCout / editRecette.nb_portions : 0;

  const handleSave = async () => {
    if (!editRecette?.nom?.trim()) { toast.error('Nom requis'); return; }
    setSaving(true);
    try {
      const payload = {
        ...editRecette,
        ingredients: editRecette.ingredients.filter(i => i.nom.trim()).map(i => ({
          ...i, quantite: parseFloat(i.quantite) || 0, cout: parseFloat(i.cout) || 0,
        })),
        nb_portions: parseInt(editRecette.nb_portions) || 1,
      };
      if (showNew) {
        await api.post('/recettes', payload);
        toast.success('Recette creee');
      } else {
        await api.put(`/recettes/${editRecette.id}`, payload);
        toast.success('Recette mise a jour');
      }
      setEditRecette(null); setShowNew(false);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!confirm('Supprimer cette recette ?')) return;
    try {
      await api.delete(`/recettes/${id}`);
      toast.success('Recette supprimee');
      onRefresh();
    } catch { toast.error('Erreur'); }
  };

  return (
    <div className="space-y-4 mt-4" data-testid="recettes-tab">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-lg font-title text-[rgb(var(--lm-text))]">Recettes & Preparations</p>
          <p className="text-xs text-[rgb(var(--lm-text-muted))]">Sauces, bases, garnitures reutilisables dans vos fiches</p>
        </div>
        <Button onClick={openNew} size="sm" className="bg-lemas-gold text-[rgb(var(--lm-bg))]" data-testid="new-recette-btn">
          <Plus size={14} className="mr-1" /> Nouvelle
        </Button>
      </div>

      {/* Recettes list */}
      {recettes.length === 0 ? (
        <div className="py-12 text-center kpi-card">
          <ChefHat size={32} className="mx-auto text-[rgb(var(--lm-text-muted))] mb-3" />
          <p className="text-[rgb(var(--lm-text-secondary))]">Aucune recette</p>
          <p className="text-xs text-[rgb(var(--lm-text-muted))] mt-1">Ajoutez vos sauces, bases et preparations</p>
        </div>
      ) : (
        <div className="space-y-1">
          {recettes.map(r => (
            <div key={r.id} className="flex items-center gap-3 px-3 py-3 rounded-lg border border-[rgb(var(--lm-border))] bg-[rgb(var(--lm-surface))] hover:bg-[rgb(var(--lm-surface-elevated))] cursor-pointer"
              onClick={() => openEdit(r)} data-testid={`recette-row-${r.id}`}>
              <ChefHat size={16} className="text-lemas-gold shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[rgb(var(--lm-text))] truncate">{r.nom}</p>
                <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">
                  {RECETTE_TYPES.find(t => t.id === r.type)?.label || r.type} · {r.ingredients?.length || 0} ing. · {r.nb_portions} portions
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-mono text-sm text-lemas-gold">{r.cout_portion?.toFixed(2)}EUR</p>
                <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">/portion</p>
              </div>
              <button onClick={e => { e.stopPropagation(); handleDelete(r.id); }} className="text-red-400/40 hover:text-red-400 shrink-0">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Edit/Create Dialog */}
      <Dialog open={!!editRecette} onOpenChange={(open) => { if (!open) { setEditRecette(null); setShowNew(false); setAcResults([]); setAcIdx(null); } }}>
        <DialogContent className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[rgb(var(--lm-text))] flex items-center gap-2">
              <ChefHat size={18} className="text-lemas-gold" />
              {showNew ? 'Nouvelle recette' : editRecette?.nom}
            </DialogTitle>
          </DialogHeader>

          {editRecette && (
            <div className="space-y-3">
              {/* Nom + Type */}
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <label className="text-[8px] text-[rgb(var(--lm-text-muted))] uppercase">Nom</label>
                  <Input value={editRecette.nom} onChange={e => setEditRecette(p => ({...p, nom: e.target.value}))}
                    placeholder="Ex: Sauce morille" className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] text-sm" data-testid="recette-nom" />
                </div>
                <div>
                  <label className="text-[8px] text-[rgb(var(--lm-text-muted))] uppercase">Type</label>
                  <select value={editRecette.type} onChange={e => setEditRecette(p => ({...p, type: e.target.value}))}
                    className="w-full h-9 text-sm bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))] rounded px-2 text-[rgb(var(--lm-text))]">
                    {RECETTE_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Nb portions */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[8px] text-[rgb(var(--lm-text-muted))] uppercase">Nb portions</label>
                  <Input type="number" value={editRecette.nb_portions} onChange={e => setEditRecette(p => ({...p, nb_portions: e.target.value}))}
                    className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] text-sm font-mono" data-testid="recette-portions" />
                </div>
                <div>
                  <label className="text-[8px] text-[rgb(var(--lm-text-muted))] uppercase">Unite portion</label>
                  <Input value={editRecette.unite_portion} onChange={e => setEditRecette(p => ({...p, unite_portion: e.target.value}))}
                    placeholder="portion, louche, cuillere"
                    className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] text-sm" />
                </div>
              </div>

              {/* Cout summary */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-lemas-gold/5 border border-lemas-gold/20">
                <div>
                  <p className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase">Cout total recette</p>
                  <p className="font-mono text-lg text-[rgb(var(--lm-text))]">{editCout.toFixed(2)}EUR</p>
                </div>
                <div className="text-right">
                  <p className="text-[9px] text-lemas-gold uppercase">Cout / portion</p>
                  <p className="font-mono text-xl text-lemas-gold font-bold">{coutPortion.toFixed(2)}EUR</p>
                </div>
              </div>

              {/* Ingredients */}
              <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase flex items-center gap-1">
                <Package size={10} /> Ingredients ({editRecette.ingredients?.length || 0})
              </p>
              {editRecette.ingredients.map((ing, i) => (
                <div key={i} className="rounded-lg bg-[rgb(var(--lm-bg))]/50 p-2 space-y-1" data-testid={`recette-ing-${i}`}>
                  <div className="flex items-center gap-1.5">
                    <div className="relative flex-1">
                      <Search size={10} className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[rgb(var(--lm-text-muted))]" />
                      <Input value={ing.nom}
                        onChange={e => searchMercuriale(i, e.target.value)}
                        onBlur={() => setTimeout(() => { if (acIdx === i) { setAcResults([]); setAcIdx(null); } }, 250)}
                        className="bg-transparent border-[rgb(var(--lm-border))] text-xs h-7 pl-6" placeholder="Chercher ingredient..." />
                      {acIdx === i && acResults.length > 0 && (
                        <div className="absolute z-30 left-0 right-0 mt-0.5 bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))] rounded-lg shadow-lg max-h-36 overflow-y-auto">
                          {acResults.map((r, ri) => (
                            <button key={ri} onClick={() => selectAc(i, r)}
                              className="w-full text-left px-3 py-1.5 hover:bg-lemas-gold/10 text-xs flex justify-between border-b border-[rgb(var(--lm-border))]/50 last:border-0">
                              <span className="truncate">{r.nom}</span>
                              <span className="font-mono text-lemas-gold shrink-0 ml-2">{r.cout?.toFixed(2)}/{r.unite}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <Input type="number" step="any" value={ing.quantite || ''} onChange={e => updateIng(i, 'quantite', e.target.value)}
                      placeholder="Qte" className="w-14 bg-transparent border-[rgb(var(--lm-border))] text-xs h-7 font-mono text-center" />
                    <select value={ing.unite || 'kg'} onChange={e => updateIng(i, 'unite', e.target.value)}
                      className="w-12 h-7 text-xs bg-transparent border border-[rgb(var(--lm-border))] rounded px-1 text-[rgb(var(--lm-text))]">
                      {['kg', 'g', 'L', 'cl', 'ml', 'piece'].map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                    <Input type="number" step="0.01" value={ing.cout || ''} onChange={e => updateIng(i, 'cout', e.target.value)}
                      placeholder="Prix" className="w-14 bg-transparent border-[rgb(var(--lm-border))] text-xs h-7 font-mono text-center" />
                    <button onClick={() => removeIng(i)} className="text-red-400/40 hover:text-red-400"><X size={12} /></button>
                  </div>
                </div>
              ))}

              <Button size="sm" onClick={addIng} variant="outline"
                className="w-full h-8 text-xs border-dashed border-[rgb(var(--lm-border))]" data-testid="recette-add-ing">
                <Plus size={12} className="mr-1" /> Ingredient
              </Button>

              {/* Instructions */}
              <div>
                <label className="text-[8px] text-[rgb(var(--lm-text-muted))] uppercase">Instructions (optionnel)</label>
                <textarea value={editRecette.instructions || ''} onChange={e => setEditRecette(p => ({...p, instructions: e.target.value}))}
                  placeholder="Etapes de preparation..."
                  className="w-full h-20 p-2 rounded-lg bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))] text-xs text-[rgb(var(--lm-text))] resize-none" />
              </div>

              <Button onClick={handleSave} disabled={saving}
                className="w-full bg-lemas-gold text-[rgb(var(--lm-bg))] hover:bg-lemas-gold/90 py-3" data-testid="save-recette-btn">
                {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
                {showNew ? 'Creer la recette' : 'Enregistrer'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ══════════════════════════════════════════════════════════════════
// MAIN HUB
// ══════════════════════════════════════════════════════════════════
const FoodCostHub = () => {
  const [activeTab, setActiveTab] = useState('carte');
  const [carteItems, setCarteItems] = useState([]);
  const [margesData, setMargesData] = useState(null);
  const [recettes, setRecettes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState('all');

  const fetchCarte = useCallback(async () => {
    try {
      const res = await api.get('/carte-items');
      if (res.data.length === 0) {
        await api.post('/carte-items/import-defaults');
        const res2 = await api.get('/carte-items');
        setCarteItems(res2.data);
      } else {
        setCarteItems(res.data);
      }
    } catch { setCarteItems([]); }
  }, []);

  const fetchMarges = useCallback(async () => {
    try {
      const res = await api.get('/marges/dashboard');
      setMargesData(res.data);
    } catch { setMargesData(null); }
  }, []);

  const fetchRecettes = useCallback(async () => {
    try {
      const res = await api.get('/recettes');
      setRecettes(res.data);
    } catch { setRecettes([]); }
  }, []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchCarte(), fetchMarges(), fetchRecettes()]);
    setLoading(false);
  }, [fetchCarte, fetchMarges, fetchRecettes]);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  if (loading) {
    return (
      <div className="flex justify-center py-20" data-testid="foodcost-loading">
        <Loader2 className="w-8 h-8 animate-spin text-lemas-gold" />
      </div>
    );
  }

  const dishes = margesData?.dishes || [];
  const kpis = margesData?.kpis || {};

  return (
    <div className="space-y-4 animate-slide-up" data-testid="foodcost-hub">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))]">
          <TabsTrigger value="carte" className="flex-1 gap-1 data-[state=active]:bg-lemas-gold data-[state=active]:text-[rgb(var(--lm-bg))] text-xs">
            <UtensilsCrossed size={13} /> Carte
          </TabsTrigger>
          <TabsTrigger value="recettes" className="flex-1 gap-1 data-[state=active]:bg-lemas-gold data-[state=active]:text-[rgb(var(--lm-bg))] text-xs">
            <ChefHat size={13} /> Recettes
          </TabsTrigger>
          <TabsTrigger value="marge" className="flex-1 gap-1 data-[state=active]:bg-lemas-gold data-[state=active]:text-[rgb(var(--lm-bg))] text-xs">
            <PieChart size={13} /> Marge
          </TabsTrigger>
        </TabsList>

        <TabsContent value="carte">
          <CarteTab items={carteItems} dishes={dishes} catFilter={catFilter} setCatFilter={setCatFilter} onRefresh={refreshAll} />
        </TabsContent>
        <TabsContent value="recettes">
          <RecettesTab recettes={recettes} onRefresh={refreshAll} />
        </TabsContent>
        <TabsContent value="marge">
          <MargeTab dishes={dishes} kpis={kpis} parCategorie={margesData?.par_categorie || {}} catFilter={catFilter} setCatFilter={setCatFilter} onRefresh={refreshAll} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FoodCostHub;
